import React, { useState, useEffect } from 'react';
import { Button, InputField, TextAreaField, colors, AlertBar } from '@dhis2/ui';
import { useRatingScaleService, DEFAULT_RATING_SCALE } from '../api/RatingScaleService';

const safeHex = (v) => {
  if (typeof v !== 'string') return '#000000';
  const trimmed = v.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed;
  if (/^[0-9a-fA-F]{6}$/.test(trimmed)) return `#${trimmed}`;
  return '#000000';
};

const withAlpha = (hex, alpha) => {
  const safe = safeHex(hex);
  const a = Math.round(alpha * 255).toString(16).padStart(2, '0');
  return `${safe}${a}`;
};

const RatingScaleAdmin = ({ orgUnitId, orgUnitName }) => {
  const { loadRatingScale, saveRatingScale } = useRatingScaleService(orgUnitId);
  const [ratingScale, setRatingScale] = useState(DEFAULT_RATING_SCALE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    if (orgUnitId) {
      loadData();
    }
  }, [orgUnitId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const scale = await loadRatingScale();
      setRatingScale(scale);
    } catch (error) {
      console.error('Failed to load rating scale:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRatingChange = (index, field, value) => {
    const updated = [...ratingScale];
    updated[index] = { ...updated[index], [field]: value };
    setRatingScale(updated);
  };

  const handleAddRating = () => {
    const newValue = ratingScale.length + 1;
    setRatingScale([
      ...ratingScale,
      { value: newValue, label: `${newValue} - New Rating`, color: '#6c757d', description: '' }
    ]);
  };

  const handleRemoveRating = (index) => {
    const updated = ratingScale.filter((_, i) => i !== index);
    // Re-number values
    const renumbered = updated.map((item, idx) => ({ ...item, value: idx + 1 }));
    setRatingScale(renumbered);
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await saveRatingScale(ratingScale);
    if (result.success) {
      setNotification({ type: 'success', message: `Rating scale saved successfully!` });
      setTimeout(() => setNotification(null), 3000);
    } else {
      setNotification({ type: 'error', message: `Failed to save: ${result.error}` });
    }
    setSaving(false);
  };

  const handleReset = () => {
    if (window.confirm('Reset rating scale to default? This will overwrite your custom settings.')) {
      setRatingScale(DEFAULT_RATING_SCALE);
    }
  };

  if (!orgUnitId) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        Loading...
      </div>
    );
  }

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading rating scale configuration...</div>;
  }

  return (
    <div style={{ marginBottom: '24px', padding: '16px', border: '1px solid #ccc', borderRadius: '8px', backgroundColor: '#fff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h4 style={{ margin: 0 }}>Rating Scale Configuration</h4>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
           small
           onClick={handleAddRating}
           style={{
              padding: '6px',
              backgroundColor: '#033ca8',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 500,
              color: 'white'
            }}
            >
            + Add Rating Level
          </button>

          <Button small destructive onClick={handleReset}>
            Reset to Default
          </Button>
        </div>
      </div>

      <p style={{ marginBottom: '16px', color: '#666', fontSize: '0.85rem' }}>
        Define how rating values are displayed to users in <strong>{orgUnitName || orgUnitId}</strong>.
      </p>

      <div style={{ marginBottom: '20px', maxHeight: '400px', overflowY: 'auto' }}>
        {ratingScale.map((rating, idx) => (
          <div
            key={idx}
            style={{
              display: 'grid',
              gridTemplateColumns: '80px 1fr 200px 1fr 60px',
              gap: '12px',
              alignItems: 'start',
              marginBottom: '12px',
              padding: '12px',
              border: '1px solid #e0e0e0',
              borderRadius: '6px',
              backgroundColor: '#fafafa'
            }}
          >
            <div>
              <InputField
                value={rating.value}
                label="Value"
                disabled
                dense
                style={{ width: '70px' }}
              />
            </div>
            <div>
              <InputField
                value={rating.label}
                onChange={({ value }) => handleRatingChange(idx, 'label', value)}
                label="Display Label"
                placeholder="e.g., 3 - Meets Expectations"
                dense
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '4px', color: '#333' }}>
                Color
              </label>
              <input
                type="color"
                value={safeHex(rating.color)}
                onChange={(e) => {
                  console.log('🎨 picker onChange', idx, e.target.value);
                  handleRatingChange(idx, 'color', e.target.value);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: 56,
                  height: 36,
                  padding: 2,
                  border: '1px solid #ccc',
                  borderRadius: 6,
                  cursor: 'pointer',
                  background: 'transparent',
                  pointerEvents: 'auto',
                  position: 'relative',
                  zIndex: 5,
                  WebkitAppearance: 'auto',
                  appearance: 'auto',
                }}
                title="Click to pick a color"
              />
            </div>
            <div>
              <TextAreaField
                value={rating.description}
                onChange={({ value }) => handleRatingChange(idx, 'description', value)}
                label="Description"
                placeholder="Brief description of this rating level"
                rows={2}
                dense
              />
            </div>
            <div style={{ paddingTop: '28px' }}>
              <Button small destructive onClick={() => handleRemoveRating(idx)} disabled={ratingScale.length <= 1}>
                Remove
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
              padding: '6px',
              backgroundColor: '#033ca8',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 500,
              color: 'white'
            }}>
          {saving ? 'Saving...' : 'Save Rating Scale'}
        </button>
      </div>

      {notification && (
        <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 10000 }}>
          <AlertBar
            {...(notification.type === 'success' ? { success: true } : { critical: true })}
            onHidden={() => setNotification(null)}
          >
            {notification.message}
          </AlertBar>
        </div>
      )}
    </div>
  );
};

export default RatingScaleAdmin;